import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { start, stop } from '../../../actions/stream';
import { selectChannel } from '../../../selectors/channelSelectors';
import useActions from '../../../utils/hooks/useActions';
import useLocalize from '../../../utils/hooks/useLocalize';
import CssFlash from '../../common/CssFlash';
import Number from '../../common/Number';
import Time from '../../common/Time';
import SectionHeaderComponent from '../SectionHeaderComponent';
import './PreviousPriceSection.scss';
import './SummarySection.scss';

const i18nKeyPrefix = 'symbol.details.previous.price.section.';
const rowTypes = ['open', 'high', 'low', 'last', 'volume'];

const getSymbolSession = (symbol, letter) => {
  return symbol.replace('-', `.${letter}-`);
};

const PreviousPriceSection = ({ symbol }) => {
  // Actions
  const [startStream] = useActions([start], []);
  const [stopStream] = useActions([stop], []);

  // Redux
  const metadata = useSelector((state) => state.symbols.data || {});

  // Intl
  const localize = useLocalize();
  const localizeWithPrefix = (id) => localize(i18nKeyPrefix + id);

  const startAllStreams = useCallback(() => {
    startStream(`${symbol}/quote`);
    startStream(`${getSymbolSession(symbol, 'M')}/quote`);
    startStream(`${getSymbolSession(symbol, 'A')}/quote`);
  }, [startStream, symbol]);

  const stopAllStreams = useCallback(() => {
    stopStream(`${symbol}/quote`);
    stopStream(`${getSymbolSession(symbol, 'M')}/quote`);
    stopStream(`${getSymbolSession(symbol, 'A')}/quote`);
  }, [stopStream, symbol]);

  const channel = useMemo(() => `${symbol}/quote`, [symbol]);
  const channelMorning = useMemo(
    () => `${getSymbolSession(symbol, 'M')}/quote`,
    [symbol]
  );
  const channelAfternoon = useMemo(
    () => `${getSymbolSession(symbol, 'A')}/quote`,
    [symbol]
  );

  const quote = useSelector((state) => selectChannel(state, channel));
  const morning = useSelector((state) => selectChannel(state, channelMorning));
  const afternoon = useSelector((state) =>
    selectChannel(state, channelAfternoon)
  );

  const symbolMetadata = (metadata[symbol] || {}).data || {};
  const symbolPrecision = symbolMetadata.precision || 0;

  useEffect(() => {
    startAllStreams();

    return () => {
      stopAllStreams();
    };
  }, [symbol, startAllStreams, stopAllStreams]);

  const rowData = useMemo(() => {
    const generateRowValueArray = (type) => {
      let timeVariableName = type + 'Time';
      let precision = symbolPrecision;
      if (type === 'volume') {
        timeVariableName = 'lastTime';
        precision = null;
      }

      let returnArray = [
        {
          value: quote[type] === 0 ? '' : quote[type],
          precision: precision
        },
        {
          value: quote[timeVariableName] === 0 ? '' : quote[timeVariableName]
        },
        {
          value: morning[type] === 0 ? '' : morning[type],
          precision: precision
        },
        {
          value:
            morning[timeVariableName] === 0 ? '' : morning[timeVariableName]
        },
        {
          value: afternoon[type] === 0 ? '' : afternoon[type],
          precision: precision
        },
        {
          value:
            afternoon[timeVariableName] === 0 ? '' : afternoon[timeVariableName]
        }
      ];

      if (type === 'last') {
        returnArray[0].value =
          quote['lastTime'] === '00:00:00' ? '' : quote['last'];
        returnArray[2].value =
          morning['lastTime'] === '00:00:00' ? '' : morning['last'];
        returnArray[4].value =
          afternoon['lastTime'] === '00:00:00' ? '' : afternoon['last'];
      }

      return returnArray;
    };

    return rowTypes.map((type) => ({
      title: type,
      values: generateRowValueArray(type)
    }));
  }, [quote, morning, afternoon, symbolPrecision]);

  const getRow = (valueObj) => {
    const valueCells = valueObj.values.filter((_, i) => i % 2 === 0);

    return (
      <div
        className="summary-detail-previous-price-table-row"
        key={valueObj.title}
      >
        <div className="summary-detail-previous-price-table-cell-title">
          {localizeWithPrefix(valueObj.title)}
        </div>
        {valueCells.map((valueCell, i) => {
          const originalIndex = i * 2;
          const timeValue = valueObj.values[originalIndex + 1]?.value;

          return (
            <div
              className={`summary-detail-previous-price-table-cell-value session-detail${
                i === valueCells.length - 1 ? ' last-column' : ''
              }`}
              key={originalIndex}
            >
              <CssFlash value={valueCell.value} isChange={false}>
                <Number
                  value={valueCell.value}
                  precision={valueCell.precision}
                />
              </CssFlash>
              {timeValue && timeValue !== '00:00:00' && (
                <span className="timestamp-inline">
                  <Time value={timeValue} />
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="summary-section">
      <SectionHeaderComponent title={localizeWithPrefix('title')} />
      <div className="summary-section-content">
        <div className="summary-detail-previous-price-table">
          <div className="summary-detail-previous-price-table-heading">
            <div className="summary-detail-previous-price-table-row">
              <div className="summary-detail-previous-price-table-head">
                &nbsp;
              </div>
              <div className="summary-detail-previous-price-table-head session-detail">
                {localizeWithPrefix('total')}
              </div>
              <div className="summary-detail-previous-price-table-head session-detail">
                {localizeWithPrefix('morning')}
              </div>
              <div className="summary-detail-previous-price-table-head session-detail">
                {localizeWithPrefix('afternoon')}
              </div>
            </div>
          </div>
          <div className="summary-detail-previous-price-table-body">
            {rowData.map((row) => getRow(row))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviousPriceSection;
